import { useCallback, useMemo, useState } from 'react'
import { Col, Row, Spinner } from 'react-bootstrap'
import classnames from 'classnames'
import { skipToken } from '@reduxjs/toolkit/query'

import { useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession, selectRoomSessionHasTranscription } from '../../../../../store/meetingSummary/roomSession'
import { useGetTranscriptionFileLocationQuery } from '../../../../../store/media/media'
import { useDownloadFileQuery } from '../../../../../store/media/file'
import { useAccount } from '../../../../../common/useAccount'
import { useMcpAppsAccess } from '../../../../../common/useMcpAppsAccess'
import { type TranscriptionResult, type TranscriptionResultResponse } from '../../../../../models/transcription'

import { Button } from '../../../../../components/Button/Button'
import { TrackEvent, useTracking } from '../../common/tracking'
import { useHeaderDownloadTranscript } from '../Header/hooks'
import { TranscriptDownloadModal, type TranscriptDownloadFeedback } from './TranscriptDownloadModal'
import { MCP_LOGOS } from '../../../../../common/mcpLogos'
import { useTranscriptDownloadPrompt } from './hooks'

import styles from './Transcript.module.scss'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'

const OAUTH_APPS_URL = `${import.meta.env.REACT_APP_INTEGRATION_REDIRECT_URL}/account/integration-settings/oauth2apps`
const PROMO_BULLETS = ['Ask questions across all your meetings', "Get answers a single transcript can't give you", 'New meetings are accessible automatically']

const SPEAKER_COLOR_CLASSES = [
  styles.speakerColor0,
  styles.speakerColor1,
  styles.speakerColor2,
  styles.speakerColor3,
  styles.speakerColor4,
  styles.speakerColor5,
  styles.speakerColor6,
  styles.speakerColor7,
]

interface SpeakerGroup {
  participantId: string
  lines: TranscriptionResult[]
}

const formatTimestamp = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds))
  const hh = Math.floor(total / 3600)
  const mm = Math.floor((total % 3600) / 60)
  const ss = total % 60
  const parts = hh > 0 ? [hh, mm, ss] : [mm, ss]
  return parts.map((value) => value.toString().padStart(2, '0')).join(':')
}

export const Transcript: React.FC = () => {
  const { accountId } = useAccount()
  const roomSession = useAppSelector(selectRoomSession)
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)

  const { downloadTranscriptLoading, handleDownloadTranscript } = useHeaderDownloadTranscript()
  const { trackingClient } = useTracking()
  const { checkShouldShowModal, recordDownloadClick, snoozeOrComplete, dismissPermanently } = useTranscriptDownloadPrompt()

  const [showModal, setShowModal] = useState<boolean>(false)

  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { mcpAppsEnabled, hasMcpConnection, probes: mcpAccessProbes } = useMcpAppsAccess()

  const { data: fileLocation, isFetching: isLocationFetching } = useGetTranscriptionFileLocationQuery(
    roomSession && hasTranscription ? { accountId, roomId: roomSession.roomId, sessionId: roomSession.id } : skipToken,
  )
  const { data: transcriptFile, isFetching: isTranscriptFetching, isError: isTranscriptError } = useDownloadFileQuery(fileLocation?.location ?? skipToken)

  const transcriptions = useMemo(() => (transcriptFile as TranscriptionResultResponse | undefined)?.transcriptions ?? [], [transcriptFile])
  const isLoading = hasTranscription && !isTranscriptError && (isLocationFetching || isTranscriptFetching || (!!fileLocation && transcriptFile === undefined))

  const speakerGroups = useMemo(
    () =>
      transcriptions.reduce<SpeakerGroup[]>((groups, line) => {
        const lastGroup = groups[groups.length - 1]
        if (lastGroup?.participantId === line.participantId) {
          lastGroup.lines.push(line)
        } else {
          groups.push({ participantId: line.participantId, lines: [line] })
        }
        return groups
      }, []),
    [transcriptions],
  )

  const speakerColorClasses = useMemo(() => {
    const uniqueParticipantIds = [...new Set(transcriptions.map((line) => line.participantId))]
    return Object.fromEntries(uniqueParticipantIds.map((participantId, index) => [participantId, SPEAKER_COLOR_CLASSES[index % SPEAKER_COLOR_CLASSES.length]]))
  }, [transcriptions])

  const redirectToOAuthApps = () => {
    window.location.href = OAUTH_APPS_URL
  }

  const handleDownloadClick = useCallback(async () => {
    trackingClient.track({ eventName: TrackEvent.TRANSCRIPT_DOWNLOAD_CLICKED, eventDetails: { accountId, currentUserId } })
    recordDownloadClick()
    const downloadCompleted = await handleDownloadTranscript()
    if (downloadCompleted && !hasMcpConnection && checkShouldShowModal()) {
      setShowModal(true)
    }
  }, [trackingClient, accountId, currentUserId, recordDownloadClick, handleDownloadTranscript, hasMcpConnection, checkShouldShowModal])

  const handleFeedback = useCallback(
    (selection: TranscriptDownloadFeedback) => {
      const variant = mcpAppsEnabled ? 'connect_mcp' : 'mcp_not_enabled'
      trackingClient.track({ eventName: TrackEvent.TRANSCRIPT_DOWNLOAD_FEEDBACK_INTERACTED, eventDetails: { selection, variant, accountId, currentUserId } })
      setShowModal(false)
      switch (selection) {
        case 'connect_mcp':
          snoozeOrComplete()
          redirectToOAuthApps()
          break
        case 'downloads_work_for_me':
          snoozeOrComplete()
          break
        case 'interested_but_not_allowed':
          dismissPermanently()
          break
        case 'closed':
        default:
          break
      }
    },
    [trackingClient, snoozeOrComplete, dismissPermanently, accountId, currentUserId, mcpAppsEnabled],
  )

  const handlePromoClick = useCallback(() => {
    trackingClient.track({ eventName: TrackEvent.PAST_MEETINGS_TRANSCRIPT_TAB_MCP_PROMO_CLICKED, eventDetails: { accountId, currentUserId } })
    redirectToOAuthApps()
  }, [trackingClient, accountId, currentUserId])

  const baseStartTime = transcriptions[0]?.startTime ?? 0
  const speakerName = (participantId: string) => roomSession?.users.find((user) => user.id === participantId)?.name ?? 'Unknown'

  return (
    <>
      <Row className='g-4'>
        <Col md={8} className='order-2 order-md-1 d-flex flex-column gap-4'>
          {transcriptions.length > 0 ? (
            <Button
              variant='grayDarkGhostButton'
              className='align-self-start d-flex align-items-center gap-2'
              onClick={handleDownloadClick}
              loading={downloadTranscriptLoading}
              disabled={!hasTranscription || isLoading}
            >
              <i className='bi bi-download' />
              Download to CSV
            </Button>
          ) : null}
          {isLoading ? (
            <div className='d-flex justify-content-center py-4'>
              <Spinner />
            </div>
          ) : transcriptions.length > 0 ? (
            <div className='d-flex flex-column'>
              {speakerGroups.map((group, groupIndex) => (
                <div key={`${group.participantId}-${groupIndex}`} className='d-flex flex-column zocks-gap-0.25'>
                  <div className='d-flex align-items-center gap-2'>
                    <span
                      className={classnames(
                        'd-flex align-items-center justify-content-center rounded-1 text-white',
                        styles.speakerAvatar,
                        speakerColorClasses[group.participantId],
                      )}
                    >
                      <i className='bi bi-person-fill' />
                    </span>
                    <span className='paragraph-medium fw-bold zocks-text-gray-900'>{speakerName(group.participantId)}</span>
                  </div>
                  <div className='d-flex flex-column zocks-gap-0.25 pt-1 zocks-pb-2'>
                    {group.lines.map((line, lineIndex) => (
                      <div key={`${line.startTime}-${lineIndex}`} className='d-flex'>
                        <span className={classnames('paragraph-medium zocks-text-gray-600', styles.timestamp)}>
                          {formatTimestamp(line.startTime - baseStartTime)}
                        </span>
                        <span className='paragraph-medium zocks-text-gray-900'>{line.transcript}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={classnames(
                'd-flex flex-column align-items-center justify-content-center text-center gap-3 p-5 border zocks-border-color-gray-300 zocks-border-0.5 bg-white',
                styles.emptyCard,
              )}
            >
              <span className={classnames('zocks-text-gray-900', styles.headingMd)}>Transcript not available</span>
              <span className='paragraph-medium zocks-text-gray-600'>This transcript was deleted in accordance with your data retention policy.</span>
            </div>
          )}
        </Col>
        <Col md={4} className='order-1 order-md-2'>
          <div
            className={classnames(
              'd-flex flex-column zocks-gap-2 p-4 zocks-bg-color-primary-light border zocks-border-color-gray-300 zocks-border-0.5',
              styles.promo,
            )}
          >
            <div className='d-flex flex-column zocks-gap-0.75'>
              <span className={classnames('zocks-text-gray-900', styles.headingMd)}>Get more from your meetings with the Zocks MCP.</span>
              <span className='paragraph-medium zocks-text-gray-900'>Give your AI tool context across every meeting. One connection. Always up to date.</span>
              <div className='d-flex flex-column gap-2'>
                {PROMO_BULLETS.map((bullet) => (
                  <div key={bullet} className='d-flex align-items-start gap-2'>
                    <i className='bi bi-check2 zocks-text-primary' />
                    <span className='paragraph-medium zocks-text-gray-900'>{bullet}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={classnames('d-flex flex-column gap-3 p-3 zocks-border-0.5', styles.promoFooter)}>
              <span className='lead-small zocks-text-gray-900'>Connect your AI tool to Zocks</span>
              <div className='d-flex align-items-center gap-2'>
                {MCP_LOGOS.map((logo) => (
                  <span
                    key={logo.alt}
                    className={classnames(
                      'd-flex align-items-center justify-content-center rounded bg-white border zocks-border-color-gray-400',
                      styles.logoBadge,
                    )}
                  >
                    <img className={styles.logoImg} src={logo.src} alt={logo.alt} />
                  </span>
                ))}
                <button type='button' className='btn btn-link p-0 paragraph-medium fw-bold zocks-text-gray-900 text-decoration-none' onClick={handlePromoClick}>
                  + Show all
                </button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      {mcpAccessProbes}
      <TranscriptDownloadModal show={showModal} mcpAppsEnabled={mcpAppsEnabled} onSelect={handleFeedback} />
    </>
  )
}
